'use client'

import Link from 'next/link'
import { useEffect, useRef } from 'react'
import { LuArrowLeft, LuEllipsis } from 'react-icons/lu'

import DropdownMenu from '@/core/components/dropdown-menu'
import ErrorToast from '@/core/components/error-toast'
import { Button, buttonStyles } from '@/core/components/form/button/button'
import Spinner from '@/core/components/spinner'
import { cn } from '@/core/utils/common'

import type { Route } from 'next'
import type { ComponentProps, ReactNode, Ref } from 'react'

/**
 * 스튜디오의 머리줄(목록 · 편집 공용) — ← · 제목 · 오른쪽 도구. 스크롤 칸 맨 위에 붙어 내용이 유리 밑으로 지나간다. 단계 이동은 아래 EditorDock 이 맡는다. 좁은 화면에서도 도구 이름을 숨기지 않는다.
 * thumb 가 null 이면 그림 칸을 두지 않는다. below 는 같은 유리 안 아랫줄(목록의 탭 줄).
 */
export default function EditorBar({
  back,
  onBack,
  backLabel,
  kind,
  title,
  thumb,
  headingRef,
  status,
  below,
  children,
}: {
  back: Route
  onBack?: () => void
  backLabel: string
  kind?: string
  title: string
  thumb?: string | null
  headingRef?: Ref<HTMLHeadingElement>
  status?: ReactNode
  below?: ReactNode
  children?: ReactNode
}) {
  return (
    <header className="glass-chrome sticky top-0 z-20 flex-none">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-3 py-3 over8:px-5">
        <div className="flex min-w-0 flex-1 basis-full items-center gap-2 over8:gap-3 over10:basis-0">
          <Link
            href={back}
            onNavigate={
              onBack
                ? (event) => {
                    event.preventDefault()
                    onBack()
                  }
                : undefined
            }
            aria-label={backLabel}
            title={backLabel}
            className={buttonStyles({
              variant: 'ghost',
              size: 'icon',
              className: 'flex-none text-gray',
            })}
          >
            <LuArrowLeft aria-hidden="true" className="size-5" />
          </Link>
          {thumb !== null && (
            <span className="hidden size-9 flex-none overflow-hidden rounded-control bg-surface-raised over6:block">
              {thumb && (
                // eslint-disable-next-line @next/next/no-img-element
                <img src={thumb} alt="" className="size-full object-cover" />
              )}
            </span>
          )}
          <div className="min-w-0 flex-1">
            <h1
              ref={headingRef}
              tabIndex={-1}
              className="truncate text-c15 leading-6 font-semibold text-foreground outline-none over10:text-c16"
            >
              {title}
            </h1>
            {kind && (
              <p className="flex min-w-0 flex-wrap items-center gap-x-1.5 text-c12 leading-4 text-description over10:text-c14 over10:leading-5">
                <span className="min-w-0 truncate">{kind}</span>
                {status && (
                  <>
                    <span aria-hidden="true" className="text-t4">
                      ·
                    </span>
                    {status}
                  </>
                )}
              </p>
            )}
          </div>
        </div>
        <div className="flex w-full flex-wrap items-center justify-end gap-1 border-t border-border pt-2 over10:w-auto over10:border-0 over10:pt-0">
          {children}
        </div>
      </div>
      {below}
    </header>
  )
}

export type StepNavItem = {
  id: string
  label: string
  required: boolean
  done: boolean
  disabled?: boolean
}

/**
 * 아래 떠 있는 유리 독 — 단계 이동의 한 곳. 단계는 번호 없이 이름만, 지금 단계는 채움으로 표시한다. 좁은 화면은 단계 줄을 옆으로 넘긴다.
 * 스크롤 칸 맨 아래에 sticky 로 붙어 내용이 유리 밑으로 지나가고, 끝까지 내리면 내용과 겹치지 않는다.
 */
export function EditorDock({
  items,
  current,
  onSelect,
}: {
  items: StepNavItem[]
  current?: string
  onSelect?: (id: string) => void
}) {
  return (
    <div className="pointer-events-none sticky bottom-0 z-20 mt-auto flex flex-none justify-center px-3 pb-[calc(16px+env(safe-area-inset-bottom,0px))] over10:pb-6">
      <div className="pointer-events-auto max-w-full rounded-full shadow-[0_16px_48px_rgb(0_0_0/0.6)]">
        <div className="glass flex max-w-full items-center gap-1 rounded-full p-1.5">
          <StepPills
            tour="steps"
            label="작성 단계"
            items={items}
            current={current}
            onSelect={onSelect}
          />
        </div>
      </div>
    </div>
  )
}

/** 단계 이름 줄 — 번호 없이 이름만, 지금 단계는 채움. 유리 독 · 캐릭터 작성 탭 · 편집 창이 같은 모양을 쓴다. 넘치면 옆으로 넘긴다 */
export function StepPills({
  items,
  current,
  onSelect,
  label,
  tour,
  size = 'md',
}: {
  items: StepNavItem[]
  current?: string
  onSelect?: (id: string) => void
  label: string
  tour?: string
  size?: 'sm' | 'md'
}) {
  const activeStep = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    activeStep.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  }, [current])

  return (
    <nav
      data-studio-tour={tour}
      aria-label={label}
      className="scrollbar-none min-w-0 overflow-x-auto [mask-image:linear-gradient(to_right,transparent,#000_10px,#000_calc(100%-10px),transparent)] over10:[mask-image:none]"
    >
      <ol className="flex items-center gap-0.5 px-2 over10:px-1">
        {items.map((item) => {
          const on = item.id === current
          return (
            <li key={item.id} className="flex-none">
              <Button
                ref={on ? activeStep : undefined}
                variant="ghost"
                size={size}
                skin="flat"
                data-focus-appearance="underline"
                aria-current={on ? 'step' : undefined}
                disabled={item.disabled}
                onClick={() => onSelect?.(item.id)}
                className={cn(
                  'group px-3! whitespace-nowrap transition-colors disabled:opacity-100! over10:px-4!',
                  on && 'bg-foreground/15!'
                )}
              >
                <span
                  className={cn(
                    'leading-5 font-semibold underline-offset-4 transition-colors duration-200 group-focus-visible:underline',
                    size === 'sm'
                      ? 'text-c13 over10:text-c14'
                      : 'text-c14 over10:text-c15',
                    on
                      ? 'text-foreground'
                      : item.disabled
                        ? 'text-description'
                        : item.done
                          ? 'text-gray group-hover:text-foreground'
                          : 'text-description group-hover:text-foreground'
                  )}
                >
                  {item.label}
                </span>
                {item.required && <span className="sr-only">필수</span>}
                {item.done && !on && <span className="sr-only">채움</span>}
              </Button>
            </li>
          )
        })}
      </ol>
    </nav>
  )
}

/**
 * 시트(레이어) 아래 막대 — 판 끝까지 붙인다. 레이어 본문의 아래 · 옆 여백(--layer-pb · --layer-px)을 음수 여백으로 덮어
 * 막대 아래로 글이 비쳐 보이지 않게 한다(댓글 입력창과 같은 방식). 면은 판과 같은 그룹 면
 */
export function SheetFooter({
  error,
  children,
}: {
  error?: string
  children: ReactNode
}) {
  return (
    <footer className="sticky -bottom-(--layer-pb) z-10 -mx-(--layer-px) mt-6 -mb-(--layer-pb) border-t border-border bg-surface px-(--layer-px) pt-3 pb-[max(var(--layer-pb),env(safe-area-inset-bottom))]">
      <ErrorToast message={error} />
      <div className="flex flex-wrap items-center justify-end gap-2">
        {children}
      </div>
    </footer>
  )
}

/** 미리보기·링크 복사 보조 도구. 이식하기·새로 만들기·저장은 이름이 보이는 별도 버튼을 쓴다. */
export function EditorMore({
  items,
  busy,
}: {
  items: ComponentProps<typeof DropdownMenu>['items']
  busy?: boolean
}) {
  return (
    <DropdownMenu
      align="end"
      items={items}
      trigger={
        <span
          data-studio-tour="more"
          className={buttonStyles({
            variant: 'ghost',
            size: 'icon',
            className: 'text-gray',
          })}
        >
          {busy ? (
            <Spinner />
          ) : (
            <LuEllipsis aria-hidden="true" className="size-5" />
          )}
          <span className="sr-only">편집 도구 더보기</span>
        </span>
      }
    />
  )
}
